import React, { useState } from "react";
import { Icon } from "./Icon";
import { WebVideoArt } from "./WebVideoArt";
import { getApiBaseUrl } from "../../api/client";
import { videoMinutes } from "../formatDuration";

export type WebVideo = {
  id: string;
  title: string;
  category: string;
  duration?: number; // seconds
  isPremium?: boolean;
  ageMin?: number;
  ageMax?: number;
  // Parent-set poster URL. May be absolute (https://…) or a relative path
  // like `/entity-binary/…` that needs the API host prefixed.
  thumbUrl?: string | null;
};

type Props = {
  video: WebVideo;
  onPlay: (v: WebVideo) => void;
  progress?: number; // 0..1 for "continue watching"
  badge?: string;
};

export function WebVideoCard({ video, onPlay, progress, badge }: Props) {
  const minutes = videoMinutes(video.duration ?? 360);
  const ageLabel =
    video.ageMin && video.ageMax ? `${video.ageMin}–${video.ageMax}` : "3–6";
  // Track <img> load failures so we can fall back to the category-art SVG
  // if the parent's poster URL 404s. Without this guard a broken thumbUrl
  // leaves a blank box instead of the friendly default.
  const [thumbErrored, setThumbErrored] = useState(false);
  const posterSrc = (() => {
    if (!video.thumbUrl || thumbErrored) return null;
    return video.thumbUrl.startsWith("http")
      ? video.thumbUrl
      : `${getApiBaseUrl()}${video.thumbUrl}`;
  })();
  return (
    <button
      className="w-card"
      onClick={() => onPlay(video)}
      // Force the button to fill its slot. The class-based `width: 100%`
      // wasn't sticking in all parents (slider items kept letting the
      // button size to its content width). Inline style wins every
      // specificity contest.
      style={{ width: "100%", minWidth: 0, maxWidth: "100%" }}
    >
      <div className="art">
        <WebVideoArt category={video.category} />
        {posterSrc && (
          <img
            src={posterSrc}
            alt=""
            onError={() => setThumbErrored(true)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              // Sits above WebVideoArt but below the badges/play button so
              // the lock/new/play overlays still read correctly.
              zIndex: 1,
            }}
          />
        )}
        {video.isPremium && (
          <div className="lock-badge">
            <Icon name="lock" size={14} />
          </div>
        )}
        {badge && <div className="new-badge">{badge}</div>}
        <div className="play">
          <Icon name="play" size={16} />
        </div>
        {typeof progress === "number" && (
          <div className="progress">
            <div style={{ width: `${progress * 100}%` }} />
          </div>
        )}
      </div>
      <div className="meta">
        <div className="cat">{video.category || "Video"}</div>
        <div className="title">{video.title}</div>
        <div className="sub">
          <span>{minutes} min</span>
          <span className="dot" />
          <span>Age {ageLabel}</span>
          {video.isPremium && (
            <>
              <span className="dot" />
              <span style={{ color: "var(--primary-ink)", fontWeight: 800 }}>Kidly+</span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

export default WebVideoCard;
